import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useResolvedList } from '../hooks/useResolvedList';
import { useEditLock } from '../hooks/useEditLock';
import { useIsOnline } from '../hooks/useIsOnline';
import { hasConflictMarker, stripConflictMarker } from '../lib/mergeConflict';
import { flagKey, isConflictBandVisible } from '../lib/conflictBand';
import { createSerialRunner } from '../lib/serialSave';
import { shouldAdvanceBase } from '../lib/baseAdvance';
import { writeListWithConflictDetection, archiveDivergedLocal, archivePreMerge } from '../lib/historyConflict';
import { decideAdoption } from '../lib/mergeOnReceive';
import { logSync, fingerprint } from '../lib/syncLog';
import { recordContent } from '../lib/contentTrail';
import { noteShrink, removedLines, countLines } from '../lib/shrinkLog';
import { getClientId } from '../lib/clientId';
import { moveLinesUp, moveLinesDown } from '../lib/moveLines';
import Wordmark from '../components/Wordmark';
import MarkdownEditor, { dispatchDocReplace } from '../components/MarkdownEditor';
import { isLivePreviewEditorActive } from '../lib/editorFlag';
import FormatMenu from '../components/FormatMenu';
import { setFormatTarget, blurFormatTarget } from '../lib/formatTarget';
import HistoryPanel from '../components/HistoryPanel';
import { MutedRemindersBand } from '../components/ListPanel';
import BoardPickerModal from '../components/BoardPickerModal';
import { useBoards, fetchBoardLists } from '../hooks/useData';
import { useSharedListPlacements } from '../hooks/useSharedListPlacements';
import { TEMPLATE_BLANK, stripTemplateContent } from '../lib/boardTemplates';

/**
 * ListPage — standalone view for a single list, used when a user
 * lands on /list/:listId from a per-list share. Cross-user resolver
 * via useResolvedList finds the list across all users' subtrees;
 * Firestore rules grant read/write based on per-list editors/viewers
 * arrays.
 *
 * Deliberately simpler than FocusedPanel — this is the entry point
 * for someone who was given access to ONE list and only needs to
 * see/edit that one list. The full FocusedPanel UX (lock,
 * reminders, move-to-board, archive, share menu) can be added in
 * a later pass; for now we render a clean focused editor with
 * title + content + role indicator.
 */
export default function ListPage({ user, reminders = [] }) {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { list, ownerUid, boardId, loading, error } = useResolvedList({
    listId,
    currentUid: user?.uid,
  });

  // Local content state for snappy typing — server updates flow back
  // through the live listener below, so we reconcile when the doc
  // changes upstream (e.g. another collaborator typed).
  const [localContent, setLocalContent] = useState('');
  const [localTitle, setLocalTitle] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [showHistory, setShowHistory] = useState(false);
  // Walter 2026-09-14 (fix 2): the conflict flag this panel just resolved.
  // "Mark resolved" clears it in one atomic write, but this panel only hears
  // about that write through its normal snapshot, which can trail by seconds,
  // so the band used to linger and appear to come back. Remembering the exact
  // flag hides it at once, while a genuinely new conflict (a new
  // serverTimestamp) still shows. lib/conflictBand.
  const [dismissedConflictKey, setDismissedConflictKey] = useState(null);
  const saveTimer = useRef(null);
  const lastServerVersion = useRef({ content: '', title: '' });
  // Versions this window has held, oldest first - what lets the stale-echo
  // guard recognise our own write coming back (#250).
  const versionHistoryRef = useRef([]);
  // Walter 2026-10-31: texts THIS WINDOW has written. `lastWriteByClientId`
  // identifies the DEVICE, and he runs two windows on one PC - so on its own
  // it would brand the other window's deliberate undo as our own echo and
  // ignore it, trading one kind of disagreement for another. Combined with
  // this set, the echo guard (#250) fires only for text we actually wrote.
  const wroteRef = useRef(new Set());
  const noteWritten = (text) => {
    const s = String(text ?? '');
    wroteRef.current.add(s);
    if (wroteRef.current.size > 8) wroteRef.current.delete(wroteRef.current.values().next().value);
  };

  const textareaRef = useRef(null);
  // CM6 live-preview branch (Walter 2026-07-19: this page was the last
  // textarea-only editor surface — shared lists looked raw here while every
  // other view rendered). Same per-device flag as the panels; no reminder
  // capture or size guard on this page (parity with its textarea).
  const mdEditorRef = useRef(null);
  const useLivePreview = isLivePreviewEditorActive();
  // Walter 2026-05-16: track the last server-confirmed updatedAt in
  // millis. Used as the baseline for conflict detection on writes.
  // See lib/historyConflict.js.
  const baselineUpdatedAtMs = useRef(null);
  // Walter 2026-06-15: 3-way conflict BASE — the raw cloud content this
  // editor's buffer descends from. Unlike lastServerVersion (which advances
  // on every snapshot, even while the user has diverged), this ONLY advances
  // when we actually adopt the server value or after our own save — so a
  // concurrent collaborator write while the user is mid-edit is still caught
  // as a content conflict. `localContentRef` mirrors localContent so the
  // sync effect can tell adopt-vs-keep without a render lag. See
  // lib/historyConflict.js (content-level detection).
  const baseContentRef = useRef('');
  // Walter 2026-09-14 (fix 2): this page's saves run one after another.
  // Held for the component's lifetime; see scheduleSave and lib/serialSave.
  const saveRunnerRef = useRef(null);
  if (!saveRunnerRef.current) saveRunnerRef.current = createSerialRunner();
  const localContentRef = useRef('');
  useEffect(() => { localContentRef.current = localContent; }, [localContent]);

  // Place-on-board subscriptions (Walter 2026-05-25). Sharees who land
  // here from a per-list share have no exposed way to "adopt" the
  // shared list into one of their own boards — placement UI lives only
  // on FocusedPanel's ⋯ menu, which this view doesn't reach. We
  // surface it directly with a banner + picker. Mirrors the
  // AcceptInvitePage placement flow.
  const { boards: userBoards, addBoardFromTemplate } = useBoards(user?.uid);
  const { placements, setPlacement } = useSharedListPlacements(user?.uid);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [placeInFlight, setPlaceInFlight] = useState(false);

  // Sync local state from server. Runs whenever the live listener
  // produces a fresh `list`. We avoid clobbering active local edits:
  // if the user has typed since the last server snapshot, keep the
  // local value; otherwise adopt the server value.
  // Walter 2026-10-31: this window now reconciles with the SAME engine as the
  // board panels. It used to carry its own rule - "if I haven't typed, take
  // the server; if I have, keep mine, for ever" - which is why his board panel
  // and this window sat disagreeing until he reloaded: once this buffer had
  // diverged it never accepted anything again, and it merged nothing. Every
  // piece of sync hardening (#157, #162, #164, #238, #250) had gone into
  // ListPanel and FocusedPanel only; the page was built "deliberately
  // lightweight" and never caught up. One engine, one place to fix.
  const setBase = (text) => {
    const next = String(text ?? '');
    baseContentRef.current = next;
    const h = versionHistoryRef.current;
    if (h.length === 0 || h[h.length - 1].text !== next) {
      h.push({ text: next, at: Date.now() });
      if (h.length > 8) h.shift();
    }
  };

  useEffect(() => {
    if (!list) return;
    const serverContent = list.content || '';
    const serverTitle = list.title || '';

    // The title has no merge semantics - it is one short string - so it keeps
    // the original rule: take the server's unless this window has edited it.
    setLocalTitle(prev => {
      if (prev === lastServerVersion.current.title || prev === '') return serverTitle;
      return prev;
    });

    const firstLoad = localContentRef.current === '' && baseContentRef.current === '';
    const decision = (effectiveReadOnly || firstLoad)
      ? { action: 'adopt', content: serverContent }
      : decideAdoption({
        base: baseContentRef.current,
        local: localContentRef.current,
        server: serverContent,
        history: versionHistoryRef.current,
        now: Date.now(),
        fromSelf: list.lastWriteByClientId === getClientId() && wroteRef.current.has(serverContent),
      });

    // Walter 2026-10-31: THIS WINDOW USED TO BE INVISIBLE. It writes through
    // writeListWithConflictDetection directly rather than useData.saveList, and
    // it called neither recordContent nor logSync - so nothing it did appeared
    // in the content trail, the sync log or the lost-text log. When his Randy
    // note vanished, both PC windows in the trail showed only `adopt`, and I
    // read that as "the PC didn't write it" and blamed his phone. He was right
    // and I was wrong: the writer simply wasn't instrumented. Never again.
    const boring = decision.action === 'keep'
      || (decision.action === 'adopt' && localContentRef.current === serverContent);
    if (!boring) {
      logSync('adopt-decision', {
        list: listId,
        surface: 'list-page',
        action: decision.action,
        base: `${baseContentRef.current.length}/${fingerprint(baseContentRef.current)}`,
        local: `${localContentRef.current.length}/${fingerprint(localContentRef.current)}`,
        server: `${serverContent.length}/${fingerprint(serverContent)}`,
        ...(decision.conflicts ? { clashes: decision.conflicts } : {}),
      });
    }

    if (decision.action === 'stale') {
      // A version we have already moved past - our own echo. Change nothing,
      // and in particular do NOT advance base onto older text (#250).
      return;
    }

    if (decision.action === 'keep') {
      // Server unchanged; adopting would only discard unsaved local edits.
      setBase(serverContent);
      lastServerVersion.current = { content: serverContent, title: serverTitle };
      return;
    }

    if (decision.action === 'conflict') {
      // Genuine same-line clash. Archive our buffer BEFORE dropping it, and
      // only adopt once that has actually landed - never trade his text for a
      // round-trip we did not confirm.
      archiveDivergedLocal({
        uid: ownerUid,
        boardId,
        listId,
        content: decision.divergedLocal,
        baseContent: baseContentRef.current,
        actorUid: user?.uid || null,
      }).then(({ historyId }) => {
        if (!historyId || localContentRef.current !== decision.divergedLocal) return;
        setLocalContent(serverContent);
        setBase(serverContent);
        lastServerVersion.current = { content: serverContent, title: serverTitle };
      }).catch((e) => {
        console.warn('[list page] archiving the diverged buffer failed (keeping it)', e);
      });
      return;
    }

    if (decision.action === 'merge') {
      // Clean three-way: keep both sides and write the result back so the
      // other surface converges on it too. The pre-merge text is archived -
      // "clean" means no overlap was detected, not that it got it right.
      archivePreMerge({
        uid: ownerUid,
        boardId,
        listId,
        content: serverContent,
        baseContent: baseContentRef.current,
        actorUid: user?.uid || null,
        source: 'list-page-merge-on-receive',
      });
      noteShrink({
        listId,
        reason: 'merge',
        removed: removedLines(localContentRef.current, decision.content),
        beforeLines: countLines(localContentRef.current),
        afterLines: countLines(decision.content),
      });
      recordContent(listId, decision.content, 'merge');
      setLocalContent(decision.content);
      localContentRef.current = decision.content;
      setBase(serverContent);
      lastServerVersion.current = { content: serverContent, title: serverTitle };
      scheduleSave({ content: decision.content });
      return;
    }

    // adopt
    noteShrink({
      listId,
      reason: 'adopt',
      removed: removedLines(localContentRef.current, decision.content),
      beforeLines: countLines(localContentRef.current),
      afterLines: countLines(decision.content),
    });
    recordContent(listId, decision.content, 'adopt');
    setLocalContent(decision.content);
    localContentRef.current = decision.content;
    setBase(serverContent);
    lastServerVersion.current = { content: serverContent, title: serverTitle };
    // Capture the cloud's updatedAt as our baseline for the next write.
    // Any future write whose target doc has a newer updatedAt indicates
    // another device wrote between this snap and our write → conflict.
    const ms = list.updatedAt?.toMillis?.();
    if (typeof ms === 'number') baselineUpdatedAtMs.current = ms;
  }, [list]);

  const isEditor = list && Array.isArray(list.editors) && user?.uid && list.editors.includes(user.uid);
  const isOwner = ownerUid && user?.uid && ownerUid === user.uid;
  const roleCanEdit = isOwner || isEditor;

  // Soft-lock — same primitive that ListPanel + FocusedPanel use, so
  // a per-list-share recipient editing on /list/:id gets the same
  // one-editor-at-a-time guarantees as a board-share recipient
  // editing in the board context. Earlier we skipped this because
  // ListPage was deliberately lightweight; Walter's test caught the
  // gap (two users on a per-list share could type simultaneously).
  // Walter 2026-05-12: require an OTHER user (not just currentUid) —
  // see ListPanel for the full rationale (inheritance copying
  // self-share into per-list ACLs).
  const hasOtherCollaborator = (uids) =>
    Array.isArray(uids) && uids.some(u => u && u !== user?.uid);
  // Walter 2026-05-16: lock always-on; per-device clientId extends
  // the protection to solo multi-device editing. See ListPanel for
  // the rationale.
  const hasSharedCollaborator = !!(
    hasOtherCollaborator(list?.editors) ||
    hasOtherCollaborator(list?.viewers)
  );
  const lockEnabled = true;
  const lock = useEditLock({
    ownerUid,
    boardId,
    list,
    currentUid: user?.uid,
    currentUserName: user?.displayName || user?.email || 'someone',
    enabled: lockEnabled,
    onKicked: () => {
      // ListPage doesn't have a top-level toast surface — show the
      // kicked state via the lock badge, which auto-updates when
      // lock.heldBy changes. The textarea also flips to readOnly
      // via effectiveReadOnly below.
    },
  });
  const canEdit = roleCanEdit;
  // Connectivity gate: shared lists are read-only when offline. See
  // ListPanel/useIsOnline for full rationale. ListPage by definition
  // is showing a per-list-share doc, so lockEnabled is essentially
  // always true here — but we still guard with it for the "owner
  // viewing their own list standalone" case (where lockEnabled may
  // be false because the list has no other ACL entries).
  const isOnline = useIsOnline();
  const offlineLockedShared = hasSharedCollaborator && !isOnline;
  const effectiveReadOnly = !roleCanEdit
    || (lockEnabled && !lock.canEdit)
    || offlineLockedShared;

  // Debounced save. Updates flow as the user types; 600ms after the
  // last keystroke we flush a partial update. Keeps Firestore writes
  // bounded even on heavy typing.
  function scheduleSave(patch) {
    if (effectiveReadOnly || !ownerUid || !boardId || !listId) return;
    setSaveStatus('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      // Walter 2026-09-14 (fix 2): queue behind any save still in flight.
      // The base is read inside the task, i.e. when it STARTS, after the
      // previous save has advanced it. Before this, a save firing while
      // another was still travelling carried the pre-save base, and the
      // conflict check read his own consecutive edits as a clash (the
      // 2026-09-13 phone band, same mechanism). What is written is unchanged.
      saveRunnerRef.current(async () => {
      try {
        // Walter 2026-05-16: route through the conflict-aware helper so
        // that if another device wrote between our baseline and now,
        // the version we'd be overwriting is snapshotted to history
        // first. No silent loss of work.
        const touchesProse = 'content' in patch || 'title' in patch;
        if (touchesProse) {
          const baseAtStart = baseContentRef.current;
          await writeListWithConflictDetection({
            uid: ownerUid,
            boardId,
            listId,
            patch,
            baselineUpdatedAtMs: baselineUpdatedAtMs.current,
            // Content-level base — the cloud content this edit descends from.
            baselineContent: 'content' in patch ? baseAtStart : undefined,
            actorUid: user?.uid || null,
          });
          // Our write is now the cloud content — advance the base so the
          // next keystroke compares against what we just saved. Walter
          // 2026-09-19 (#238): but never backwards over an adoption that
          // landed while we were writing. See lib/baseAdvance.
          if ('content' in patch && shouldAdvanceBase(baseAtStart, baseContentRef.current)) {
            baseContentRef.current = patch.content ?? '';
            noteWritten(patch.content ?? '');
          }
          if ('content' in patch) {
            // The write itself, in both logs - this window's blind spot (above).
            recordContent(listId, patch.content ?? '', 'save');
            logSync('saved', { list: listId, surface: 'list-page', chars: (patch.content ?? '').length });
          }
        } else {
          const ref = doc(db, 'users', ownerUid, 'boards', boardId, 'lists', listId);
          await updateDoc(ref, {
            ...patch,
            updatedAt: serverTimestamp(),
            lastWriteByClientId: getClientId(),
          });
        }
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(prev => (prev === 'saved' ? 'idle' : prev)), 1200);
      } catch (err) {
        console.warn('[ListPage] save failed', err);
        setSaveStatus('idle');
      }
      });
    }, 600);
  }

  function onContentChange(e) {
    if (effectiveReadOnly) return;
    const v = e.target.value;
    setLocalContent(v);
    scheduleSave({ content: v });
    // Heartbeat the lock — keeps the active-typing window fresh so
    // other users don't try to take over while we're still editing.
    if (lockEnabled) lock.touch();
  }

  function onTitleChange(e) {
    if (effectiveReadOnly) return;
    const v = e.target.value;
    setLocalTitle(v);
    scheduleSave({ title: v });
    if (lockEnabled) lock.touch();
  }

  // Format-menu commits (Walter 2026-07-19: the standalone page gets the
  // format strip too). Textarea: same shape as the panels' formatCommit.
  // CM6: dispatch to the view (the #132/#136 pattern); onChange then syncs
  // localContent + schedules the save. No notifyContentChange either way —
  // this page has no reminder-capture engine (the bell button is hidden).
  const taFormatCommit = (next, selStart, selEnd) => {
    setLocalContent(next);
    scheduleSave({ content: next });
    if (lockEnabled) lock.touch();
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) { try { ta.setSelectionRange(selStart, selEnd); } catch { /* noop */ } }
    });
  };
  const cmFormatCommit = (next, selStart, selEnd) => {
    const view = mdEditorRef.current?.view;
    if (!view) return;
    dispatchDocReplace(view, next, selStart, selEnd);
    if (lockEnabled) lock.touch();
  };

  function onTextareaFocus() {
    // Same as ListPanel — try to acquire the lock when the textarea
    // gains focus. If acquire fails (someone else holds it freshly),
    // readOnly stays true via lock.canEdit and the badge surfaces
    // who's typing.
    if (lockEnabled) lock.acquire();
  }
  function onTextareaBlur() {
    // Release on click-away (Walter 2026-05-04). The "editing
    // shared list" band hides automatically since it's tied to
    // lock.heldByMe.
    if (lockEnabled && lock.heldByMe) lock.release();
  }
  // CONTENT editor focus/blur — the title input shares the lock handlers
  // above but must NOT register as the format target (the menu would edit
  // content while you type the title). Content registers; title doesn't.
  function onContentFocus() {
    setFormatTarget({
      listId,
      ...(useLivePreview ? { cmRef: mdEditorRef, commit: cmFormatCommit } : { textareaRef, commit: taFormatCommit }),
      isReminderList: false,
      readOnly: effectiveReadOnly,
    });
    onTextareaFocus();
  }
  function onContentBlur() {
    blurFormatTarget(listId); // greys the format menu
    onTextareaBlur();
  }

  // Flush on unmount so a fast nav-away doesn't drop the last typed
  // character. updateDoc is async; we don't await on unmount because
  // React doesn't give us an async cleanup hook, but the request
  // goes out before the component fully unmounts.
  useEffect(() => () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }
  }, []);

  // Place-on-board handler. Mirrors AcceptInvitePage.handlePickBoard
  // and BoardPage.handlePlaceListPick — same shape (boardId or
  // createWithName), same stair-step position math against the
  // target board's existing tiles, same setSharedListPlacement write.
  // On success we navigate via window.location.href so the target
  // board mounts cleanly (matches the accept-flow pattern — mid-flight
  // subscriptions need a clean boot to re-resolve placements).
  const isSharedWithMe = list && ownerUid && user?.uid && ownerUid !== user.uid;
  const hasPlacement = !!(list && placements && placements[list.id]);
  // "shared by <name>" under the title (Walter 2026-07-19: this page showed
  // only the ED/viewer chip — no clue WHOSE list you're in). The name comes
  // from the user's own sharedWithMe pointer doc (cachedOwnerName, written at
  // accept time); best-effort — falls back to a generic label.
  const [sharedByName, setSharedByName] = useState('');
  useEffect(() => {
    if (!isSharedWithMe || !user?.uid || !listId) { setSharedByName(''); return; }
    getDoc(doc(db, 'users', user.uid, 'sharedWithMe', listId))
      .then(s => setSharedByName((s.exists() && s.data().cachedOwnerName) || ''))
      .catch(() => setSharedByName(''));
  }, [isSharedWithMe, user?.uid, listId]);
  const handlePickBoard = async (pick) => {
    if (!list || !ownerUid || !boardId || !user?.uid) {
      throw new Error('missing context to place this list');
    }
    setPlaceInFlight(true);
    try {
      let targetBoardId = pick.boardId;
      let targetLists = [];
      if (pick.createWithName) {
        const seed = stripTemplateContent(TEMPLATE_BLANK);
        const res = await addBoardFromTemplate(seed, pick.createWithName);
        targetBoardId = res?.boardId;
      } else if (targetBoardId) {
        try {
          targetLists = await fetchBoardLists(user.uid, targetBoardId);
        } catch (err) {
          console.warn('[ListPage.placement] fetchBoardLists failed; using default position', err);
        }
      }
      if (!targetBoardId) throw new Error('could not resolve a target board');
      // Walter 2026-05-26: position the placed tile at top-left and
      // sort it FIRST. AcceptInvitePage / BoardPage's place handlers
      // both stair-step the cursor and append at maxOrder + 1, which
      // is fine when the user has zero context for where the tile
      // landed — but here the user explicitly invoked placement and
      // their intuition is "I just placed it, it should be the first
      // thing I see." So we use (80, 60) for x/y (the same top-left
      // anchor a fresh list takes) and `minOrder - 1` for order so
      // it sorts before everything else on the destination board.
      // z still bumps above the current max so it sits on top in any
      // overlap until the user drags it.
      const maxZ = targetLists.reduce((m, l) => Math.max(m, l.z || 0), 0);
      const minOrder = targetLists.reduce(
        (m, l) => (l.order != null && l.order < m ? l.order : m),
        Infinity,
      );
      const firstOrder = minOrder === Infinity ? 1 : minOrder - 1;
      await setPlacement(list.id, {
        sourceOwnerUid: ownerUid,
        sourceBoardId: boardId,
        boardId: targetBoardId,
        // Best-effort cache for the per-tile "shared by" band — the
        // resolved list doesn't carry the owner's display name, but
        // it's not fatal: the band falls back to a generic "shared
        // with you" when sourceOwnerName is empty.
        sourceOwnerName: '',
        sourceCachedTitle: (list.title || '').trim(),
        x: 80,
        y: 60,
        w: 280,
        h: 320,
        z: maxZ + 1,
        order: firstOrder,
      });
      setPickerOpen(false);
      // Full reload so the destination board's placements + lists
      // resubscribe cleanly. Same approach AcceptInvitePage uses.
      window.location.href = `/board/${targetBoardId}`;
    } catch (err) {
      console.error('[ListPage.placement] failed:', err);
      setPlaceInFlight(false);
      throw err; // let BoardPickerModal surface the error inline
    }
  };

  if (loading) {
    return <Centered>loading...</Centered>;
  }
  if (error === 'no-access') {
    return <Centered>
      <h2 style={headingStyle}>can't open this list</h2>
      <p style={textStyle}>you don't have access — the owner may have revoked it.</p>
      <BackButton onClick={() => navigate('/')} label="go to listicity" />
    </Centered>;
  }
  if (error === 'deleted') {
    return <Centered>
      <h2 style={headingStyle}>this list was deleted</h2>
      <BackButton onClick={() => navigate('/')} label="go to listicity" />
    </Centered>;
  }
  if (!list) {
    return <Centered>
      <h2 style={headingStyle}>list not found</h2>
      <BackButton onClick={() => navigate('/')} label="go to listicity" />
    </Centered>;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      fontFamily: 'var(--mono)',
      color: 'var(--text)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Top bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 20px',
        borderBottom: '1px solid var(--border)',
      }}>
        <button
          // Walter's s1 (round 6): back-link target depends on
          // ownership. Own lists go back to the parent board (since
          // own lists belong to a board the user has access to).
          // Shared lists go back to /shared-with-me — that's the
          // recipient's home for shared content.
          onClick={() => navigate(isOwner && boardId ? `/board/${boardId}` : '/shared-with-me')}
          style={topBtn}
          title={isOwner ? 'back to board' : 'back to shared with me'}
        >
          {isOwner ? '← back to board' : '← shared with me'}
        </button>
        {/* Format strip/dropdown — same store as the board header's menu,
            CENTERED in the bar (Walter 2026-07-19). The two flex spacers
            balance the back button against the right-side cluster; the menu
            floats between them. Greyed until the content editor is focused.
            Reminder button hidden: no capture engine here. */}
        <div style={{ flex: 1 }} />
        {!effectiveReadOnly && <FormatMenu hideReminder />}
        <div style={{ flex: 1 }} />
        {/* Role badge — only shown for SHARED lists. For own lists
            the user is the owner and the badge ('editor' / 'viewer')
            doesn't add information; Walter said skip it. */}
        {!isOwner && (
          <span style={{
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: 'var(--text-dimmer)',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '2px 6px',
          }}>
            {canEdit ? 'editor' : 'viewer'}
          </span>
        )}
        {/* Walter 2026-05-16: previous-versions icon + button.
            Walter 2026-05-17: tied to conflictPendingReview flag
            (with legacy marker check as fallback) so it disappears
            once the user resolves. */}
        {isConflictBandVisible({ flag: list?.conflictPendingReview, dismissedKey: dismissedConflictKey, hasMarker: hasConflictMarker(localContent) }) && (
          <button
            onClick={() => setShowHistory(true)}
            style={{
              ...topBtn,
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
            title={`${list.historyCount} previous version${list.historyCount === 1 ? '' : 's'} — click to view`}
            aria-label="previous versions"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 5.5a4 4 0 1 1 1.2 2.85"
                stroke="currentColor" strokeWidth="1.3"
                strokeLinecap="round" fill="none"
              />
              <path
                d="M2 2.5v3h3"
                stroke="currentColor" strokeWidth="1.3"
                strokeLinecap="round" strokeLinejoin="round"
                fill="none"
              />
            </svg>
            history
          </button>
        )}
        <span style={{
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: saveStatus === 'saved' ? 'var(--accent)' : 'var(--text-dimmer)',
          minWidth: '50px',
          textAlign: 'right',
        }}>
          {saveStatus === 'saving' ? 'saving…' : saveStatus === 'saved' ? '✓ saved' : ''}
        </span>
      </div>

      {/* Wordmark + title */}
      <div style={{
        padding: '24px 20px 8px',
        maxWidth: '900px',
        width: '100%',
        margin: '0 auto',
      }}>
        <div style={{ marginBottom: '14px' }}>
          <Wordmark />
        </div>
        <input
          type="text"
          value={localTitle}
          onChange={onTitleChange}
          readOnly={effectiveReadOnly}
          onFocus={onTextareaFocus}
          onBlur={onTextareaBlur}
          placeholder="(untitled list)"
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(20px * var(--ui-scale, 1))',
            fontWeight: 600,
            color: 'var(--text)',
            padding: '6px 0',
          }}
        />
        {/* Attribution for incoming shares — matches the sidebar row's
            "shared by <name>" wording. Walter 2026-07-19. */}
        {isSharedWithMe && (
          <div style={{
            fontFamily: 'var(--mono)',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: 'var(--text-dim)',
            padding: '0 0 2px',
          }}>
            shared by {sharedByName || 'someone'}
          </div>
        )}
      </div>

      {/* Offline banner — see ListPanel for full rationale. Wins
          over the viewer / lock badges below. */}
      {offlineLockedShared && (
        <div style={{
          maxWidth: '900px',
          width: '100%',
          margin: '0 auto',
          padding: '8px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          background: 'var(--surface2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
        }}>
          <span style={{ color: 'var(--accent)' }}>📶</span>
          <span style={{ flex: 1 }}>
            you're offline — shared lists are read-only until you reconnect
          </span>
        </div>
      )}

      {/* Conflict band — see ListPanel for full rationale. */}
      {isConflictBandVisible({ flag: list?.conflictPendingReview, dismissedKey: dismissedConflictKey, hasMarker: hasConflictMarker(localContent) }) && (
        <div style={{
          maxWidth: '900px',
          width: '100%',
          margin: '0 auto',
          padding: '8px 20px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '10px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          background: 'var(--surface2)',
          border: '1px solid var(--accent-dim)',
          borderRadius: 'var(--radius)',
        }}>
          <span style={{ color: 'var(--accent)' }}>🔀</span>
          <span style={{ flex: 1 }}>
            conflicting edits shown at top of this list — review and dismiss
          </span>
          <button
            onClick={() => {
              const stripped = stripConflictMarker(localContent);
              setLocalContent(stripped);
              scheduleSave({ content: stripped });
            }}
            disabled={effectiveReadOnly}
            title="remove the conflict marker block"
            style={{
              background: 'transparent',
              color: 'var(--accent)',
              border: '1px solid var(--accent-dim)',
              borderRadius: 'var(--radius)',
              padding: '4px 10px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              cursor: effectiveReadOnly ? 'not-allowed' : 'pointer',
              opacity: effectiveReadOnly ? 0.5 : 1,
            }}
          >
            dismiss
          </button>
        </div>
      )}

      {/* Self-editing badge — when YOU hold the lock, with a
          manual "done, release" escape hatch. */}
      {/* Walter 2026-05-16: shared-only "you are editing" band. */}
      {!offlineLockedShared && roleCanEdit && hasSharedCollaborator && lock.heldByMe && (
        <div style={{
          maxWidth: '900px',
          width: '100%',
          margin: '0 auto',
          padding: '8px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          background: 'var(--surface2)',
          border: '1px solid var(--accent-dim)',
          borderRadius: 'var(--radius)',
        }}>
          <span style={{ color: 'var(--accent)' }}>✏️</span>
          <span style={{ flex: 1 }}>editing shared list</span>
          <button
            onClick={() => lock.release()}
            title="release the lock so someone else can edit"
            style={{
              background: 'transparent',
              color: 'var(--accent)',
              border: '1px solid var(--accent-dim)',
              borderRadius: 'var(--radius)',
              padding: '4px 10px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              cursor: 'pointer',
            }}
          >
            done, release
          </button>
        </div>
      )}

      {/* Lock badge — same shape as ListPanel/FocusedPanel. Renders
          when soft-lock is engaged AND another user holds it. The
          take-over button reuses the existing primitive's force-
          acquire path. Active-typing state disables the button so
          we don't yank the lock mid-keystroke from the other user.
          Skipped offline (banner above is the primary surface). */}
      {!offlineLockedShared && roleCanEdit && lockEnabled && lock.heldBy && !lock.heldByMe && (
        <div style={{
          maxWidth: '900px',
          width: '100%',
          margin: '0 auto',
          padding: '8px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          background: 'var(--surface2)',
          border: '1px solid var(--accent-dim)',
          borderRadius: 'var(--radius)',
        }}>
          <span style={{ color: 'var(--accent)' }}>🔒</span>
          <span style={{ flex: 1 }}>
            {lock.heldBy.isOtherDevice
              ? `your other device is ${lock.heldBy.activelyTyping ? 'typing' : 'editing'}`
              : `${lock.heldBy.name} ${lock.heldBy.activelyTyping ? 'is typing' : 'is editing'}`}
          </span>
          <button
            onClick={async () => {
              if (lock.heldBy?.activelyTyping) return;
              await lock.acquire({ force: true });
              // Walter 2026-05-16: belt-and-braces resync after
              // take-over. The lastServerVersion-based sync effect
              // above usually catches this, but explicit sync on
              // take-over removes any race where the user's local
              // value matches the OLD server snapshot and the new
              // one hasn't arrived yet.
              setLocalContent(list?.content || '');
            }}
            disabled={lock.heldBy?.activelyTyping}
            title={lock.heldBy?.activelyTyping
              ? 'wait until they pause typing'
              : 'take over editing'}
            style={{
              background: 'transparent',
              color: lock.heldBy?.activelyTyping ? 'var(--text-dimmer)' : 'var(--accent)',
              border: '1px solid ' + (lock.heldBy?.activelyTyping ? 'var(--border)' : 'var(--accent-dim)'),
              borderRadius: 'var(--radius)',
              padding: '4px 10px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              cursor: lock.heldBy?.activelyTyping ? 'not-allowed' : 'pointer',
              opacity: lock.heldBy?.activelyTyping ? 0.6 : 1,
            }}
          >
            take over
          </button>
        </div>
      )}

      {/* Viewer banner — shown when role doesn't permit writes (the
          user is on the list's viewers array but not editors). Keeps
          symmetry with ListPanel/FocusedPanel. Skipped offline. */}
      {!offlineLockedShared && !roleCanEdit && lockEnabled && (
        <div style={{
          maxWidth: '900px',
          width: '100%',
          margin: '0 auto',
          padding: '8px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          background: 'var(--surface2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
        }}>
          <span style={{ color: 'var(--accent)' }}>👁</span>
          <span style={{ flex: 1 }}>
            view-only — only editors can change this list
          </span>
        </div>
      )}

      {/* Place-on-board band — Walter 2026-05-25. Sharees on /list/:id
          have no other exposed way to "adopt" a shared list onto one
          of their own boards (the "place in board…" affordance lives
          in FocusedPanel's ⋯ menu which this view doesn't reach).
          Renders only when (a) the current user is the sharee (not
          the owner) and (b) the list isn't already placed on one of
          their boards. Disappears the moment a placement record
          appears in the user's subcollection, even before navigation
          completes. */}
      {isSharedWithMe && !hasPlacement && (
        <div style={{
          maxWidth: '900px',
          width: '100%',
          margin: '12px auto 0',
          padding: '0 20px',
        }}>
          <div style={{
            padding: '10px 14px',
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            color: 'var(--text-dim)',
          }}>
            <span style={{ flex: 1 }}>
              add this list to one of your boards so it sits with your
              own lists.
            </span>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={placeInFlight}
              style={{
                padding: '6px 12px',
                background: placeInFlight ? 'var(--accent-dim)' : 'var(--accent)',
                color: 'var(--bg)',
                border: '1px solid var(--accent)',
                borderRadius: 'var(--radius)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                fontWeight: 600,
                cursor: placeInFlight ? 'wait' : 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {placeInFlight ? '…' : 'place in board…'}
            </button>
          </div>
        </div>
      )}

      {/* Muted reminders band — Walter 2026-05-25 / Personal-reminders
          model. Sharees landing on /list/:id from a per-list share see
          the same "reminders here are silent for you" indicator they'd
          see on ListPanel/FocusedPanel, so the "I see 🔔s but they're
          not mine" semantic is consistent across all the places a
          shared list can be viewed. Renders nothing when count === 0.
          Wrapper padding matches the body block below (20px horiz)
          so the band's left/right edges align with the textarea —
          without it the band spans wider than the editor and reads
          as an unrelated page-level banner instead of a list-scoped
          one. */}
      <div style={{
        maxWidth: '900px',
        width: '100%',
        margin: '0 auto',
        padding: '0 20px',
      }}>
        <MutedRemindersBand
          content={localContent}
          listId={list?.id}
          reminders={reminders}
        />
      </div>

      {/* Content body */}
      <div style={{
        flex: 1,
        padding: '0 20px 32px',
        maxWidth: '900px',
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {useLivePreview ? (
          <div style={{
            flex: 1,
            minHeight: '60vh',
            width: '100%',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            // Padding lives in the editor content (--md-editor-pad) so the
            // scrollbar hugs the box edge — same treatment as the panels
            // (#153).
            padding: 0,
            '--md-editor-pad': '14px',
            boxSizing: 'border-box',
            overflow: 'hidden',
            fontSize: 'calc(13px * var(--ui-scale, 1))',
            lineHeight: 1.55,
            opacity: effectiveReadOnly ? 0.7 : 1,
          }}>
            <MarkdownEditor
              ref={mdEditorRef}
              listId={listId}
              value={localContent}
              readOnly={effectiveReadOnly}
              onChange={(raw, meta) => {
                if (effectiveReadOnly) return;
                // #239 (Walter's rule): text that arrived from the cloud is
                // displayed, not written back. See MarkdownEditor's remoteSync.
                if (meta && meta.remote) return;
                setLocalContent(raw);
                scheduleSave({ content: raw });
                if (lockEnabled) lock.touch();
              }}
              onFocus={onContentFocus}
              onBlur={onContentBlur}
            />
          </div>
        ) : (
        <textarea
          ref={textareaRef}
          value={localContent}
          onChange={onContentChange}
          readOnly={effectiveReadOnly}
          onFocus={onContentFocus}
          onBlur={onContentBlur}
          onKeyDown={(e) => {
            if (e.altKey && e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
              if (effectiveReadOnly) return;
              const ta = textareaRef.current;
              if (!ta) return;
              const fn = e.key === 'ArrowUp' ? moveLinesUp : moveLinesDown;
              const r = fn(localContent, ta.selectionStart, ta.selectionEnd);
              if (!r) { e.preventDefault(); return; }
              e.preventDefault();
              setLocalContent(r.nextValue);
              scheduleSave({ content: r.nextValue });
              requestAnimationFrame(() => {
                const cur = textareaRef.current;
                if (cur) cur.setSelectionRange(r.nextSelStart, r.nextSelEnd);
              });
              if (lockEnabled) lock.touch();
            }
          }}
          placeholder={canEdit ? 'type here…' : '(empty)'}
          style={{
            flex: 1,
            minHeight: '60vh',
            width: '100%',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            color: 'var(--text)',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(13px * var(--ui-scale, 1))',
            lineHeight: 1.55,
            padding: '14px',
            resize: 'vertical',
            outline: 'none',
            cursor: effectiveReadOnly ? 'not-allowed' : 'text',
            opacity: effectiveReadOnly ? 0.7 : 1,
          }}
        />
        )}
      </div>

      {/* Walter 2026-05-25: place-on-board picker. Reuses the same
          BoardPickerModal AcceptInvitePage + ListPanel ⋯-menu use, so
          the picker UX (existing-board dropdown OR new-board name) is
          consistent across every place placement is offered. */}
      <BoardPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handlePickBoard}
        boards={userBoards || []}
        listTitle={localTitle || list?.title}
        ownerName=""
        title="place in board…"
      />

      {/* Walter 2026-05-16: previous-versions side-by-side modal. */}
      <HistoryPanel
        open={showHistory}
        onClose={() => setShowHistory(false)}
        uid={ownerUid}
        boardId={boardId}
        listId={listId}
        listTitle={localTitle || list?.title}
        currentContent={localContent}
        currentTitle={localTitle}
        actorUid={user?.uid}
        onResolved={() => setDismissedConflictKey(flagKey(list?.conflictPendingReview))}
        onApplyContent={(newContent, newTitle) => {
          if (typeof newContent === 'string') {
            setLocalContent(newContent);
          }
          if (typeof newTitle === 'string') {
            setLocalTitle(newTitle);
          }
          const patch = {};
          if (typeof newContent === 'string') patch.content = newContent;
          if (typeof newTitle === 'string') patch.title = newTitle;
          if (Object.keys(patch).length > 0) scheduleSave(patch);
        }}
      />
    </div>
  );
}

const topBtn = {
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--text-dim)',
  padding: '6px 10px',
  borderRadius: 'var(--radius)',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(11px * var(--ui-scale, 1))',
  cursor: 'pointer',
};

const headingStyle = {
  fontFamily: 'var(--mono)',
  fontSize: 'calc(16px * var(--ui-scale, 1))',
  color: 'var(--text)',
  margin: '0 0 8px',
};

const textStyle = {
  fontFamily: 'var(--mono)',
  fontSize: 'calc(12px * var(--ui-scale, 1))',
  color: 'var(--text-dim)',
  margin: '0 0 20px',
};

function Centered({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      color: 'var(--text)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px',
      fontFamily: 'var(--mono)',
    }}>
      <div style={{ textAlign: 'center', maxWidth: '420px' }}>
        {children}
      </div>
    </div>
  );
}

function BackButton({ onClick, label }) {
  return (
    <button onClick={onClick} style={{
      background: 'var(--accent)',
      color: '#fff',
      border: 'none',
      padding: '8px 14px',
      borderRadius: 'var(--radius)',
      fontFamily: 'var(--mono)',
      fontSize: 'calc(12px * var(--ui-scale, 1))',
      cursor: 'pointer',
    }}>
      {label}
    </button>
  );
}
